import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/atoms/button';
import { CameraAccessDefault } from '@components/atoms/camera-access-default';
import { Typography } from '@components/atoms/typography';
import { useCameraConstraints } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/hooks/use-camera-constraints';
import type { InfoProps } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/selfie/liveness/step/info/types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAppTheme } from '@theme';
import Lottie from 'lottie-react';

import animation from './animation.json';

export const Info = ({ onSubmit }: InfoProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isCameraNotAllowed, setIsCameraNotAllowed] = useState(false);
    const { t } = useTranslation();
    const { cameraConstraints } = useCameraConstraints();
    const theme = useAppTheme();
    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`);

    const handleNextClick = async () => {
        try {
            setIsLoading(true);
            const stream = await navigator.mediaDevices.getUserMedia(cameraConstraints);

            const tracks = stream.getTracks();

            tracks?.forEach((track) => {
                track.stop();
            });
            onSubmit();
        } catch {
            setIsCameraNotAllowed(true);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Stack sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1 }}>
            {isCameraNotAllowed ? (
                <CameraAccessDefault
                    onClose={() => {
                        setIsCameraNotAllowed(false);
                    }}
                />
            ) : (
                <Stack>
                    <Box
                        sx={{
                            width: '216px',
                            height: '208px',
                            mx: 'auto',
                            mt: 1,
                        }}
                    >
                        <Lottie animationData={animation} loop={true} />
                    </Box>
                    <Typography
                        variant='text-medium'
                        sx={{
                            mt: 0.5,
                            lineHeight: '28px !important',
                            textAlign: 'center',
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {t('docVerificationWidget.liveness.info.requirements')}
                    </Typography>
                </Stack>
            )}
            {!isCameraNotAllowed && (
                <Button
                    variant='primary'
                    sx={{ mt: 2, width: matchesMobileSm ? '100%' : undefined }}
                    size='xl'
                    onClick={handleNextClick}
                    loading={isLoading}
                >
                    {t('common.start')}
                </Button>
            )}
        </Stack>
    );
};
